import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { map, startWith } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';
import { PanelHostService } from '../../../../services/panel-host.service';
import { TripFinanceService } from '../../../../services/trip-finance.service';
import { TripService } from '../../../../services/trip.service';
import { DonationLinkInput, NewRaiserWithAssignments } from '../../../../models/finance-models';
import { ParticipantAssignmentEditorComponent } from '../../../trip-finances/participant-assignment-editor/participant-assignment-editor.component';
import { formatDateForInput } from '../../../trip-finances/trip-finance-date.util';
import { getTripRoster } from '../../../trip-finances/trip-roster.util';
import { ContentContainerComponent } from '../../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-edit-fundraiser-panel',
  imports: [ReactiveFormsModule, TranslocoDirective, ParticipantAssignmentEditorComponent, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './edit-fundraiser-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditFundraiserPanelComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.group({
    date: ['', Validators.required],
    location: ['', Validators.required],
    goal: [0, [Validators.required, Validators.min(0)]],
    raised: [0, [Validators.min(0)]],
    description: [''],
  });

  assignEnabled = signal(0);
  links = signal<DonationLinkInput[]>([]);
  submitting = signal(false);
  isEdit = signal(false);
  recordId = signal<number | null>(null);

  roster = computed(() => getTripRoster(this.tripService.currentTrip.value()));

  readonly raisedAmount = toSignal(
    this.form.controls.raised.valueChanges.pipe(
      startWith(this.form.controls.raised.value),
      map((value) => Number(value ?? 0)),
    ),
    { initialValue: 0 },
  );

  assignmentValid = computed(() => {
    if (!this.assignEnabled()) {
      return true;
    }
    const assigned = this.links().reduce((s, l) => s + l.amount, 0);
    return Math.abs(this.raisedAmount() - assigned) < 0.01;
  });

  constructor() {
    effect(() => {
      const panel = this.panelHost.activePanel();
      const context = this.panelHost.tripFinancePanelContext();
      if (panel === 'edit-fundraiser' && context?.mode === 'fundraiser') {
        this.isEdit.set(context.action === 'edit');
        if (context.action === 'edit' && context.record) {
          const record = context.record as NewRaiserWithAssignments;
          this.recordId.set(record.id);
          this.form.patchValue({
            date: formatDateForInput(record.date),
            location: record.location,
            goal: record.goal,
            raised: record.raised,
            description: record.description,
          });
          this.assignEnabled.set(record.assignFunds ?? 0);
          this.links.set(record.links.map((l) => ({ userId: l.userId, amount: l.amount })));
        } else {
          this.recordId.set(null);
          this.form.reset({
            date: formatDateForInput(new Date()),
            location: '',
            goal: 0,
            raised: 0,
            description: '',
          });
          this.assignEnabled.set(0);
          this.links.set([]);
        }
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  save(): void {
    if (this.form.invalid || this.submitting() || !this.assignmentValid()) {
      this.form.markAllAsTouched();
      return;
    }

    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    const body = {
      date: this.form.value.date!,
      location: this.form.value.location ?? '',
      goal: Number(this.form.value.goal),
      raised: Number(this.form.value.raised ?? 0),
      description: this.form.value.description ?? '',
      assignFunds: this.assignEnabled(),
      links: this.links(),
    };

    this.submitting.set(true);
    const request$ =
      this.isEdit() && this.recordId()
        ? this.tripFinanceService.updateFundraiser(trip.id, this.recordId()!, body)
        : this.tripFinanceService.createFundraiser(trip.id, body);

    request$.pipe(finalize(() => this.submitting.set(false))).subscribe({
      next: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(
            marker(this.isEdit() ? 'trip-finances.panels.fundraiser.save-success-edit' : 'trip-finances.panels.fundraiser.save-success-add')
          ),
          duration: 4000,
          color: 'success',
        });
        await toast.present();
        this.panelHost.dismiss();
      },
      error: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(marker('trip-finances.panels.save-error')),
          duration: 4000,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }
}
